import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { getCurrentLayout } from "../appLayout";
import { DOCKED_CANVAS_ID, DOCK_SLOTS } from "../docking/dockTypes";
import { dockStateStore, dockLoadersStore } from "../docking/dockStore";
import { useVisualizerStore } from "./useVisualizerStore";
import ActionTypes from "../../misc/actionTypes";

// jQuery is what the rest of the app triggers UPDATE_ALL_ITEMS on.
import $ from "jquery";
import { USE_NEW_CANVAS } from "../../config";

export interface DockedVisualizerInput {
  layers: any;
  layout: any;
  layoutType: string;
  canvasId: string;
  width: number;
  height: number;
  useMouseHovering: boolean;
  useTouchZoom: boolean;
  enableLoader: any;
  disableLoader: any;
}

/**
 * The ONE place the docked canvas gets its input — the direct analog of v3's
 * useVisualizerLayers(). Replaces the old per-page `visualizerInputStore` that
 * three different <DockCanvas/> feeders raced to write.
 *
 * Layers come from the shared layout singleton (`getCurrentLayout().getLayers()`),
 * exactly like every page did. We recompute them whenever the app signals a
 * canvas change — Redux `canvasCount` (bumped by updateCanvasCount) or the legacy
 * `UPDATE_ALL_ITEMS` window event — so a new `layers` array is handed to the
 * canvas and it redraws (its componentWillReceiveProps redraws on layers change).
 *
 * The canvasId is the single stable DOCKED_CANVAS_ID; `useMouseHovering` is on
 * only for the fullscreen (visualizer) slot, matching the old per-page value
 * (materials/cart passed useMouseHovering={false}).
 */
export function useDockedVisualizerInput(): DockedVisualizerInput | null {
  const canvasDimensions = useSelector((s: any) => s.bathroomVisualizerState.canvasDimensions);
  const canvasCount = useSelector((s: any) => s.bathroomVisualizerState.canvasCount);
  const bvVisited = useSelector((s: any) => s.bathroomVisualizerState.bvVisited);
  const visualizersLoaded = useSelector((s: any) => s.serverState.visualizersLoaded);
  const dockState = useVisualizerStore(dockStateStore.subscribe, dockStateStore.getSnapshot);
  const dockLoaders = useVisualizerStore(dockLoadersStore.subscribe, dockLoadersStore.getSnapshot);

  // Bump a local tick on the legacy layer-mutation event so getLayers() re-runs
  // even when nothing in Redux changed.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const onUpdate = () => setTick((t) => t + 1);
    $(window).on(ActionTypes.UPDATE_ALL_ITEMS, onUpdate);
    $(window).on(ActionTypes.CHANGE_LAYOUT, onUpdate);
    return () => {
      $(window).off(ActionTypes.UPDATE_ALL_ITEMS, onUpdate);
      $(window).off(ActionTypes.CHANGE_LAYOUT, onUpdate);
    };
  }, []);

  return useMemo(() => {
    const layout = getCurrentLayout();

    // Single gate for the whole feature — must match visualizer.js (canvasId)
    // and appLayout.getCurrentCanvas so the shell renders the docked canvas
    // exactly when those two also select it. USE_NEW_CANVAS already excludes the
    // look journey. If USE_NEW_CANVAS is off, the shell stays inert and pages use
    // their legacy per-page canvas.
    if (!USE_NEW_CANVAS || !layout) return null;

    const showPlaceHolders = (document as any).bvConfig ? (document as any).bvConfig.showPlaceholders : false;
    const layers = layout.getLayers(showPlaceHolders);

    // Loader callbacks are page-specific (materials' setMaterialsCanvasLoading,
    // etc.) and get registered per slot by <DockCanvas/> (see dockStore's
    // dockLoadersStore) — hand the active slot's pair to the single canvas.
    const activeLoader = dockLoaders[dockState.activeSlotName] || {};

    return {
      layers,
      layout,
      layoutType: layout.getCurrentLayoutName(),
      canvasId: DOCKED_CANVAS_ID,
      width: canvasDimensions.width,
      height: canvasDimensions.height,
      // hover/click only on the interactive (fullscreen) visualizer slot.
      useMouseHovering: dockState.activeSlotName === DOCK_SLOTS.visualizer,
      useTouchZoom: true,
      enableLoader: activeLoader.enableLoader,
      disableLoader: activeLoader.disableLoader,
    };
    // canvasCount / tick are intentional recompute triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canvasDimensions.width,
    canvasDimensions.height,
    canvasCount,
    tick,
    bvVisited,
    dockState.activeSlotName,
    visualizersLoaded.length,
    dockLoaders,
  ]);
}

export default useDockedVisualizerInput;
